import * as THREE from 'three';

const STREAK_COUNT = 110;
const STREAK_RADIUS_MIN = 3;
const STREAK_RADIUS_MAX = 22;
/** How far ahead of the ship streaks spawn — ship's local -Z is forward. */
const SPAWN_AHEAD_MIN = 220;
const SPAWN_AHEAD_MAX = 460;
/** Past this distance behind the ship a streak respawns ahead. */
const DESPAWN_BEHIND = 60;

const _tmpPos = new THREE.Vector3();
const _tmpScale = new THREE.Vector3();
const _identityQuat = new THREE.Quaternion();
const _tmpMat = new THREE.Matrix4();

/**
 * Hyperspace tunnel effect. A field of bright additive streaks anchored to
 * the ship's local frame so they always flow past from in front. Each streak
 * is a thin cylinder oriented along the ship's forward axis; the instance
 * matrix is updated every frame to slide them backward, giving the classic
 * "stars stretching to lines as you approach light speed" look.
 */
export class WarpStreaks {
  readonly object3D: THREE.InstancedMesh;

  private readonly localPositions: Float32Array;
  private readonly streakLengths: Float32Array;
  private active = false;
  /** Ramps from 0 → 1 on activation and 1 → 0 on deactivation; we scale
   *  streak opacity and length by it so the effect fades in/out instead of
   *  popping. */
  private intensity = 0;
  private readonly material: THREE.MeshBasicMaterial;

  constructor() {
    const geo = new THREE.CylinderGeometry(0.06, 0.06, 1, 5, 1);
    // Default cylinder runs along Y; rotate so its length is along Z, which
    // is what the per-instance scale.z then stretches.
    geo.rotateX(Math.PI / 2);

    this.material = new THREE.MeshBasicMaterial({
      color: 0xd2eaff,
      transparent: true,
      opacity: 0.0,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      toneMapped: false
    });

    this.object3D = new THREE.InstancedMesh(geo, this.material, STREAK_COUNT);
    this.object3D.frustumCulled = false;
    this.object3D.visible = false;
    this.object3D.name = 'warp-streaks';

    this.localPositions = new Float32Array(STREAK_COUNT * 3);
    this.streakLengths = new Float32Array(STREAK_COUNT);
    for (let i = 0; i < STREAK_COUNT; i++) this.respawn(i);
    this.flushInstances();
  }

  start(): void {
    if (this.active) return;
    this.active = true;
    this.object3D.visible = true;
    // Re-randomize on each start so the player doesn't see leftover positions
    // from the previous jump.
    for (let i = 0; i < STREAK_COUNT; i++) this.respawn(i);
    this.flushInstances();
  }

  stop(): void {
    this.active = false;
    // Don't hide immediately — let the fade-out finish in update().
  }

  /** Tick each frame. `flowSpeed` is the apparent passing speed of the
   *  streaks (faster than ship speed for that "rushing past" feel). */
  update(flowSpeed: number, dt: number): void {
    const targetIntensity = this.active ? 1 : 0;
    const k = 1 - Math.exp(-8 * dt);
    this.intensity += (targetIntensity - this.intensity) * k;

    if (!this.active && this.intensity < 0.005) {
      this.intensity = 0;
      this.object3D.visible = false;
      this.material.opacity = 0;
      return;
    }
    this.object3D.visible = true;
    this.material.opacity = 0.95 * this.intensity;

    const localPos = this.localPositions;
    const lengths = this.streakLengths;
    const flow = flowSpeed * 1.4 * dt;
    const lengthScale = 0.5 + 0.5 * this.intensity;

    for (let i = 0; i < STREAK_COUNT; i++) {
      const base = i * 3;
      let oz = (localPos[base + 2] ?? 0) + flow;
      if (oz > DESPAWN_BEHIND) {
        this.respawn(i);
        oz = localPos[base + 2] ?? 0;
      } else {
        localPos[base + 2] = oz;
      }

      _tmpPos.set(localPos[base] ?? 0, localPos[base + 1] ?? 0, oz);
      // Stretch with intensity so streaks visibly grow as the effect ramps in.
      _tmpScale.set(1, 1, (lengths[i] ?? 0) * lengthScale);
      _tmpMat.compose(_tmpPos, _identityQuat, _tmpScale);
      this.object3D.setMatrixAt(i, _tmpMat);
    }
    this.object3D.instanceMatrix.needsUpdate = true;
  }

  dispose(): void {
    this.object3D.geometry.dispose();
    this.material.dispose();
    if (this.object3D.parent) this.object3D.parent.remove(this.object3D);
  }

  private respawn(i: number): void {
    const angle = Math.random() * Math.PI * 2;
    const radius =
      STREAK_RADIUS_MIN + Math.random() * (STREAK_RADIUS_MAX - STREAK_RADIUS_MIN);
    const ahead =
      SPAWN_AHEAD_MIN + Math.random() * (SPAWN_AHEAD_MAX - SPAWN_AHEAD_MIN);
    this.localPositions[i * 3] = Math.cos(angle) * radius;
    this.localPositions[i * 3 + 1] = Math.sin(angle) * radius;
    // Negative Z is "ahead" in the ship's local frame (Three.js convention).
    this.localPositions[i * 3 + 2] = -ahead;
    this.streakLengths[i] = 6 + Math.random() * 18;
  }

  private flushInstances(): void {
    for (let i = 0; i < STREAK_COUNT; i++) {
      const base = i * 3;
      _tmpPos.set(
        this.localPositions[base] ?? 0,
        this.localPositions[base + 1] ?? 0,
        this.localPositions[base + 2] ?? 0
      );
      _tmpScale.set(1, 1, this.streakLengths[i] ?? 0);
      _tmpMat.compose(_tmpPos, _identityQuat, _tmpScale);
      this.object3D.setMatrixAt(i, _tmpMat);
    }
    this.object3D.instanceMatrix.needsUpdate = true;
  }
}
